import React, { useRef } from "react";
import {
  Modal,
  ModalContent,
  ModalOverlay,
  Spinner,
} from "@chakra-ui/react";
import { FiBarChart2, FiInfo } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { CriteriaNav } from "./CriteriaNav";
import { ConsensusPanel } from "./ConsensusPanel";
import type { ConsensusPanelHandle } from "./ConsensusPanel";
import { ScoreComparisonTable } from "./ScoreComparisonTable";
import { CurrentScoresModal } from "./CurrentScoresModal";
import { IncompleteCompletionModal } from "./IncompleteCompletionModal";
import { InstructionsBox } from "./InstructionsBox";
import { ScoringInstructionsPanel } from "../../../InitialEvaluationStep/ScoringInstructionsPanel";
import { OverallAwardView } from "../OverallAward/OverallAwardView";
import { useQueryClient } from "@tanstack/react-query";
import { useCriteriaNavigation } from "../../hooks/useCriteriaNavigation";
import { useMeetingScores, meetingScoresKeys } from "../../hooks/useMeetingScores";
import { useMeetingScoresProgress, meetingScoresProgressKeys } from "../../hooks/useMeetingScoresProgress";
import { upsertConsensusScores } from "../../api/meeting-api";
import { useScoringInstructions } from "newui/features/Rfx/Evaluation/hooks/useScoringInstructions";
import type { CriteriaId, CriteriaListItem, UpsertConsensusScoresRequest } from "../../types/meeting.type";

interface MeetingModalProps {
  evalId: number;
  isCompleting?: boolean;
  isOpen: boolean;
  rfxId: number;
  onClose: () => void;
  onCompleteMeeting?: () => void;
}

export const MeetingModal: React.FC<MeetingModalProps> = ({
  isOpen,
  onClose,
  rfxId,
  evalId,
  onCompleteMeeting,
  isCompleting = false,
}) => {
  const queryClient = useQueryClient();

  // Bootstrap criteria list from progress endpoint
  const { data: progressData, isLoading: progressLoading } =
    useMeetingScoresProgress(rfxId, evalId);

  const [criteriaList, setCriteriaList] = React.useState<CriteriaListItem[]>([]);
  const { currentCriteriaId, goNext, goPrev, goTo } = useCriteriaNavigation({
    criteriaList,
  });
  const consensusRef = useRef<ConsensusPanelHandle>(null);

  // Scoring guide — floating panel anchored to hovered cell
  const { instructions, isLoading: instructionsLoading } =
    useScoringInstructions({ rfxId });
  const [activeScore, setActiveScore] = React.useState<number | null>(null);
  const [focusedElement, setFocusedElement] = React.useState<HTMLElement | null>(null);
  const [guidePanelOpen, setGuidePanelOpen] = React.useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Sub-modals
  const [isCurrentScoresOpen, setIsCurrentScoresOpen] = React.useState(false);
  const [isIncompleteModalOpen, setIsIncompleteModalOpen] = React.useState(false);

  const handleActiveScoreChange = React.useCallback(
    (score: number | null, element: HTMLElement | null) => {
      setActiveScore(score);
      if (element) {
        setFocusedElement(element);
      }
    },
    []
  );

  // Consensus panel only updates the active score (no anchor element change)
  const handleConsensusScoreChange = React.useCallback(
    (score: number | null) => {
      setActiveScore(score);
    },
    []
  );

  // Populate criteria list from progress endpoint (bootstrap)
  React.useEffect(() => {
    if (progressData?.criteria_list?.length && criteriaList.length === 0) {
      setCriteriaList(progressData.criteria_list);
    }
  }, [progressData?.criteria_list, criteriaList.length]);

  const flushConsensus = React.useCallback(() => {
    consensusRef.current?.flush();
  }, []);

  const handleGoTo = (id: CriteriaId) => {
    flushConsensus();
    goTo(id);
  };

  const isOverall = currentCriteriaId === "overall";

  const { data: scoresData, isLoading: scoresLoading, upsertScores } = useMeetingScores(
    rfxId,
    evalId,
    isOverall ? undefined : currentCriteriaId
  );

  // Keep criteria list in sync with score navigation data (has fresh is_complete flags)
  React.useEffect(() => {
    if (scoresData?.navigation?.criteria_list?.length) {
      setCriteriaList(scoresData.navigation.criteria_list);
    }
  }, [scoresData?.navigation?.criteria_list]);

  const handleGoNext = () => { flushConsensus(); goNext(); };
  const handleGoPrev = () => { flushConsensus(); goPrev(); };

  const navProps = {
    currentCriteriaId,
    criteriaList,
    goNext: handleGoNext,
    goPrev: handleGoPrev,
    goTo: handleGoTo,
  };

  // Calculate scoring progress from criteria list
  const completedCount = criteriaList.filter((c) => c.is_complete).length;
  const totalCount = criteriaList.length;
  const allComplete = totalCount > 0 && completedCount === totalCount;
  const incompleteCriteria = criteriaList.filter((c) => !c.is_complete);

  // Complete meeting — gate behind incomplete check
  const handleCompleteMeetingClick = React.useCallback(() => {
    flushConsensus();
    if (allComplete) {
      onCompleteMeeting?.();
    } else {
      setIsIncompleteModalOpen(true);
    }
  }, [allComplete, flushConsensus, onCompleteMeeting]);

  const handleIncompleteConfirm = React.useCallback(
    (_reason: string) => {
      // TODO: send reason to backend for audit logging once endpoint supports it
      setIsIncompleteModalOpen(false);
      onCompleteMeeting?.();
    },
    [onCompleteMeeting]
  );

  const isLoading = progressLoading || (scoresLoading && !isOverall);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        as="div"
        display="flex"
        flexDirection="column"
        height="100vh"
        maxHeight="100vh"
        margin={0}
        padding={0}
        borderRadius={0}
        overflow="hidden"
        maxWidth="100vw"
      >
        {/* Top nav — flex-shrink-0 */}
        <div className="flex-shrink-0 border-b border-gray-200">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-4">
              <h2 className="text-base font-semibold text-gray-900">Panel Meeting</h2>
              {/* Scoring progress indicator */}
              {totalCount > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-24 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${allComplete ? "bg-green-500" : "bg-blue-500"
                        }`}
                      style={{ width: `${(completedCount / totalCount) * 100}%` }}
                    />
                  </div>
                  <span className={`text-xs font-medium ${allComplete ? "text-green-600" : "text-gray-500"}`}>
                    {completedCount}/{totalCount}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              {/* View current scores */}
              {!isOverall && (
                <button
                  type="button"
                  onClick={() => setIsCurrentScoresOpen(true)}
                  className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                  data-testid="view-current-scores"
                >
                  <FiBarChart2 size={13} />
                  Current Scores
                </button>
              )}
              {/* Scoring guide toggle — only show on criteria views */}
              {!isOverall && instructions && (
                <button
                  type="button"
                  onClick={() => setGuidePanelOpen((prev) => !prev)}
                  className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${guidePanelOpen
                      ? "bg-blue-100 text-blue-700"
                      : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    }`}
                  data-testid="toggle-scoring-guide"
                >
                  <FiInfo size={13} />
                  Scoring Guide
                </button>
              )}
              {onCompleteMeeting && (
                <Button
                  btntype="primary"
                  type="button"
                  onClick={handleCompleteMeetingClick}
                  disabled={isCompleting}
                  isLoading={isCompleting}
                  data-testid="complete-meeting-btn"
                  className="!py-1.5 !px-3 !text-sm"
                >
                  Complete Meeting
                </Button>
              )}
              <button
                onClick={onClose}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
          <CriteriaNav {...navProps} />
        </div>

        {/* Scrollable middle area — flex-1 overflow-y-auto */}
        <div className="flex-1 overflow-y-auto" ref={contentRef}>
          <InstructionsBox />

          {isOverall ? (
            <div className="p-6">
              <OverallAwardView rfxId={rfxId} evalId={evalId} />
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner size="lg" />
            </div>
          ) : scoresData ? (
            <ScoreComparisonTable
              data={scoresData}
              onActiveScoreChange={handleActiveScoreChange}
            />
          ) : (
            <div className="p-6 text-center text-gray-400 text-sm">
              No scores available for this criterion.
            </div>
          )}

          {/* Floating scoring guide panel */}
          <ScoringInstructionsPanel
            instructions={instructions}
            isLoading={instructionsLoading}
            activeScore={activeScore}
            isOpen={guidePanelOpen}
            onToggle={() => setGuidePanelOpen(false)}
            anchorElement={focusedElement}
            containerElement={contentRef.current}
          />
        </div>

        {/* Bottom nav — flex-shrink-0 */}
        <div className="flex-shrink-0 border-t border-gray-200">
          <CriteriaNav {...navProps} />
        </div>

        {/* ConsensusPanel — fixed at bottom, only when viewing a specific criterion */}
        {!isOverall && scoresData && (
          <div className="flex-shrink-0 border-t border-gray-200">
            <ConsensusPanel
              ref={consensusRef}
              data={scoresData}
              onSave={async (criteriaId, payload) => {
                await upsertConsensusScores(rfxId, evalId, criteriaId, payload);
                queryClient.invalidateQueries({
                  queryKey: meetingScoresKeys.scores(rfxId, evalId, criteriaId),
                });
                queryClient.invalidateQueries({
                  queryKey: meetingScoresProgressKeys.progress(rfxId, evalId),
                });
              }}
              onActiveScoreChange={handleConsensusScoreChange}
            />
          </div>
        )}
        {/* Current Scores overview modal */}
        <CurrentScoresModal
          rfxId={rfxId}
          evalId={evalId}
          isOpen={isCurrentScoresOpen}
          onClose={() => setIsCurrentScoresOpen(false)}
        />

        {/* Incomplete completion warning modal */}
        <IncompleteCompletionModal
          isOpen={isIncompleteModalOpen}
          incompleteCriteria={incompleteCriteria}
          isSubmitting={isCompleting}
          onClose={() => setIsIncompleteModalOpen(false)}
          onConfirm={handleIncompleteConfirm}
        />
      </ModalContent>
    </Modal>
  );
};
